import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MicrophoneIcon, StopIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

// iOS Safari records audio/mp4; desktop and Android Chrome record webm/opus.
function getSupportedAudioMimeType() {
    if (typeof MediaRecorder === 'undefined') return '';
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac'];
    return candidates.find(type => MediaRecorder.isTypeSupported(type)) || '';
}

function formatSeconds(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Records a spoken answer and hands the blob to the parent, which uploads it.
 * Recording stops on its own at maxSeconds so uploads stay under the 25MB the
 * transcription API accepts.
 */
export default function VoiceAnswerRecorder({ onRecorded, maxSeconds = 180, status = null, accentColor = '#1e40af' }) {
    const { t } = useTranslation();
    const [isRecording, setIsRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [audioUrl, setAudioUrl] = useState(null);
    const [error, setError] = useState(null);

    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const timerRef = useRef(null);
    const audioUrlRef = useRef(null);
    const elapsedRef = useRef(0);

    const stopTracks = () => {
        streamRef.current?.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        clearInterval(timerRef.current);
        timerRef.current = null;
    };

    useEffect(() => {
        return () => {
            stopTracks();
            if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
        };
    }, []);

    const stopRecording = () => {
        if (recorderRef.current?.state === 'recording') {
            recorderRef.current.stop();
        }
    };

    const startRecording = async () => {
        setError(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const mimeType = getSupportedAudioMimeType();
            const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            recorderRef.current = recorder;
            chunksRef.current = [];

            recorder.ondataavailable = (event) => {
                if (event.data?.size > 0) chunksRef.current.push(event.data);
            };

            recorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });

                if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
                audioUrlRef.current = URL.createObjectURL(blob);
                setAudioUrl(audioUrlRef.current);

                setIsRecording(false);
                stopTracks();
                onRecorded(blob, elapsedRef.current);
            };

            recorder.start();
            setIsRecording(true);
            setElapsed(0);
            elapsedRef.current = 0;
            timerRef.current = setInterval(() => {
                elapsedRef.current += 1;
                setElapsed(elapsedRef.current);
                if (elapsedRef.current >= maxSeconds) stopRecording();
            }, 1000);
        } catch (err) {
            console.error('Microphone access failed:', err);
            stopTracks();
            setError(t('Questionnaires.Viewer.VoiceMicrophoneDenied'));
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-3">
                {!isRecording ? (
                    <button
                        type="button"
                        onClick={startRecording}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-medium"
                        style={{ backgroundColor: accentColor }}
                    >
                        {audioUrl ? <ArrowPathIcon className="w-5 h-5" /> : <MicrophoneIcon className="w-5 h-5" />}
                        {audioUrl ? t('Questionnaires.Viewer.VoiceRecordAgain') : t('Questionnaires.Viewer.VoiceStartRecording')}
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={stopRecording}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600 text-white text-sm font-medium"
                    >
                        <StopIcon className="w-5 h-5" />
                        {t('Questionnaires.Viewer.VoiceStopRecording')}
                    </button>
                )}

                {isRecording && (
                    <span className="flex items-center gap-2 text-sm text-red-600">
                        <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
                        {formatSeconds(elapsed)} / {formatSeconds(maxSeconds)}
                    </span>
                )}
            </div>

            {audioUrl && !isRecording && (
                <audio src={audioUrl} controls className="w-full" />
            )}

            {status === 'uploading' && (
                <p className="text-sm text-gray-500">{t('Questionnaires.Viewer.VoiceUploading')}</p>
            )}
            {status === 'uploaded' && (
                <p className="text-sm text-green-600">{t('Questionnaires.Viewer.VoiceSaved')}</p>
            )}
            {status === 'failed' && (
                <p className="text-sm text-red-600">{t('Questionnaires.Viewer.VoiceUploadFailed')}</p>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
    );
}
